import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Button, Input, FormGroup } from "reactstrap";
import SelectField from "../Utils/SelectField";
import { MenuItem } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { formatCurrency } from "../Utils/Currency";

import { useDispatch, useSelector } from "react-redux";
import { getAllSO } from "../../slices/SO/GetSO/thunk";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { useColumnSettings } from "../Utils/useColumnSettings";

import SOForm from "../SO/SOForm";
import SOStockOutForm from "../SO/SOStockOutForm";
import { Loader } from "../Utils/Loader";

function NotificationSO() {
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isApprove, setIsApprove] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [stockOutModalOpen, setStockOutModalOpen] = useState(false);
  const [stockOutData, setStockOutData] = useState(null);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const soList = useSelector((state) => state.GetSO.soList || []);

  const statusOptions = [
    { label: "All", value: "waiting for approval,approved" },
    { label: "Waiting for Approval", value: "waiting for approval" },
    { label: "Approved", value: "approved" },
  ];
  const [statusFilter, setStatusFilter] = useState(statusOptions[0]);

  useEffect(() => {
    dispatch(
      getAllSO(
        pagination.pageSize,
        pagination.pageIndex + 1,
        statusFilter.value,
        setLoading,
      ),
    );
  }, [dispatch, pagination.pageSize, pagination.pageIndex, statusFilter.value]);

  const toggleModal = () => {
    setModalOpen(!modalOpen);

    if (modalOpen) {
      setEditData(null);
      setIsApprove(false);
      setIsEdit(false);
    }
  };

  const handleEdit = (rowData, parentRow) => {
    setEditData({
      orderId: parentRow.soNumber,
      locationName: {
        label: parentRow.location,
        value: parentRow.location,
      },
      status: {
        label: "Waiting Approval",
        value: "waiting for approval",
      },
      items: [
        {
          itemName: {
            label: rowData.item,
            value: rowData.item,
          },
          sku: rowData.sku,
          orderedQty: rowData.orderedQty,
          approvedQty: rowData.orderedQty,
          unitPrice: rowData.unitPrice,
          itemId: rowData.itemid,
          itemCount: rowData.netQty,
          total: rowData.total,
          notificationid: rowData.notificationid,
        },
      ],
    });

    setIsApprove(true);
    setIsEdit(true);
    setModalOpen(true);
  };

  const soData = useMemo(() => {
    const formatted = (soList.salesOrders || [])
      .map((so, index) => ({
        id: index + 1,
        soNumber: so.orderid,
        location: so.fromlocationname || "-",
        locationId: so.fromlocation || "-",

        itemData: (so.items || [])
          .filter(
            (item) =>
              item.status?.toLowerCase() === "waiting for approval" ||
              item.status?.toLowerCase() === "approved",
          )
          .map((item) => ({
            id: item.id,
            sku: item.sku,
            itemid: item.itemid,
            item: item.itemname,
            orderedQty: item.orderedquantity,
            approvedquantity: item.approvedquantity || 0,
            unitPrice: item.unitprice,
            netQty: item.itemcount,
            total: item.totalprice,
            fromlocation: so.fromlocationname || "-",
            fromLocationId: so.fromlocation,
            notificationid: item.notificationid,
            status:
              item.status === "waiting for approval"
                ? "Waiting Approval"
                : item.status === "approved"
                  ? "Approved"
                  : item.status,

            action:
              item.status === "waiting for approval"
                ? "approve"
                : item.status === "approved"
                  ? "stockout"
                  : null,
          })),
      }))
      .filter((so) => so.itemData.length > 0);

    const result = formatted.map((so) => ({
      ...so,
      items: so.itemData.length,
      totalAmt: so.itemData.reduce(
        (sum, item) => sum + Number(item.total || 0),
        0,
      ),
    }));

    if (!searchText.trim()) return result;

    const search = searchText.toLowerCase();

    return result.filter((so) => so.soNumber?.toLowerCase().includes(search));
  }, [soList, searchValue]);

  const renderStatus = (status) => {
    const statusStyles = {
      "Waiting Approval": {
        bg: "#FFF8E1",
        color: "#F9A825",
        icon: "ri-error-warning-fill",
      },

      Approved: {
        bg: "#E8F5E9",
        color: "#2E7D32",
        icon: "ri-checkbox-circle-fill",
      },
    };

    const style = statusStyles[status];

    return (
      <span
        style={{
          background: style?.bg,
          color: style?.color,
          padding: "4px 8px",
          fontSize: "13px",
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          borderRadius: "4px",
        }}
      >
        <i className={`${style?.icon} me-1`} />
        {status}
      </span>
    );
  };

  const renderAction = (action, status, rowData, parentRow) => {
    const actionStyles = {
      approve: {
        bg: "#FF8A50",
        color: "#fff",
        icon: "ri-check-double-line",
        label: "Approval Pending",
      },

      stockout: {
        bg: "#FF8A50",
        color: "#fff",
        icon: "ri-download-2-line",
        label: "Stock Out",
      },
    };

    const style = actionStyles[action];

    if (!style) return null;

    return (
      <Button
        size="sm"
        onClick={() => {
          if (action === "approve") {
            handleEdit(rowData, parentRow);
            setIsApprove(true);
          }

          if (action === "stockout") {
            setStockOutData({
              itemName: rowData.item,
              itemId: rowData.itemid,
              sku: rowData.sku,
              quantity: rowData.netQty,
              stockOutQty: rowData.approvedquantity || rowData.netQty,
              fromlocation: rowData.fromlocation,
              fromlocationId: rowData.fromLocationId,
              soNumber: parentRow.soNumber,
            });

            setStockOutModalOpen(true);
          }
        }}
        style={{
          background: style.bg,
          color: style.color,
          border: "none",
          borderRadius: "4px",
          padding: "6px 8px",
          fontSize: "13px",
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className={style.icon}></i>
        {style.label}
      </Button>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "soNumber",
        header: "Sales Order Number",
        size: 220,
        minSize: 220,
        maxSize: 220,
      },
      {
        accessorKey: "location",
        header: "Location",
        size: 180,
        minSize: 180,
        maxSize: 180,
      },
      {
        accessorKey: "items",
        header: "No. of Items",
        size: 120,
        minSize: 120,
        maxSize: 120,
      },
      {
        accessorKey: "totalAmt",
        header: "Total Amount",
        size: 150,
        minSize: 150,
        maxSize: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
    ],
    [],
  );

  const itemColumns = useMemo(
    () => [
      { accessorKey: "sku", header: "SKU" },
      { accessorKey: "item", header: "Product" },
      { accessorKey: "netQty", header: "Available Quantity" },
      { accessorKey: "orderedQty", header: "Requested Quantity" },
      { accessorKey: "approvedquantity", header: "Approved Quantity" },
      {
        accessorKey: "unitPrice",
        header: "Unit Price",
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "total",
        header: "Total",
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => renderStatus(cell.getValue()),
      },

      {
        accessorKey: "action",
        header: "Actions",
        Cell: ({ cell, row }) =>
          renderAction(
            cell.getValue(),
            row.original.status,
            row.original,
            row.original.parentData,
          ),
      },
    ],
    [],
  );

  const { toggleSettingsModal, CustomViewModal } = useColumnSettings(
    "salesorder",
    columns,
  );

  return (
    <React.Fragment>
      <div id="csms">
        <Container fluid>
          <Row>
            {/* SEARCH + SETTINGS (UNCHANGED UI) */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
              <div className="d-flex align-items-center w-100 gap-2">
                <Input
                  type="text"
                  placeholder="Search by Sales Order Number..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSearchValue(searchText);
                    }
                  }}
                  style={{
                    maxWidth: "500px",
                    height: "36px",
                    borderRadius: "4px",
                  }}
                />

                <Button
                  className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 text-nowrap"
                  onClick={() => setSearchValue(searchText)}
                >
                  <i className="ri-search-line label-icon align-middle fs-16 me-1 fw-bold"></i>
                  <span className="ps-2 ms-2 me-1 fw-bold">Search</span>
                </Button>
              </div>

              {/* <div className="d-flex align-items-center">
                <div
                  className="btn-icon cursor-img"
                  onClick={toggleSettingsModal}
                >
                  <i className="ri-settings-2-line fs-4"></i>
                </div>
              </div> */}

              <div className="d-flex align-items-center">
                <div style={{ width: "70px" }}>
                  <spna>Filter By :</spna>
                </div>
                <div
                  style={{ minWidth: "180px", marginBottom: "0px !important" }}
                >
                  <SelectField
                    value={statusFilter}
                    placeholder="Select Status"
                    options={statusOptions}
                    handleChange={(option) => setStatusFilter(option)}
                    isClearable={false}
                    isRVStyle={true}
                  />
                </div>
              </div>
            </div>

            {/* TABLE (UNCHANGED STRUCTURE) */}
            <Col lg={12}>
              <div
                className="border table-responsive"
                style={{ borderRadius: "4px" }}
              >
                {loading && <Loader />}

                <MaterialReactTable
                  columns={columns}
                  data={soData}
                  state={{
                    pagination,
                  }}
                  onPaginationChange={setPagination}
                  manualPagination
                  rowCount={soList?.totalCount || 0}
                  columnResizeMode="on"
                  defaultColumn={{
                    minSize: 40,
                    maxSize: 1000,
                    size: 150,
                  }}
                  muiTableProps={{
                    sx: {
                      tableLayout: "fixed",
                      width: "100%",
                    },
                  }}
                  enableTopToolbar={false}
                  enableDensityToggle={false}
                  enableColumnActions={false}
                  enableFullScreenToggle={false}
                  enableColumnFilters={false}
                  enableExpandAll={false}
                  enableExpanding
                  paginateExpandedRows={false}
                  positionExpandColumn="first"
                  displayColumnDefOptions={{
                    "mrt-row-expand": {
                      header: "",
                      size: 50,
                      minSize: 50,
                      maxSize: 50,
                    },
                  }}
                  muiExpandButtonProps={({ row }) => ({
                    children: row.getIsExpanded() ? (
                      <i className="ri-arrow-up-s-line fs-5"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line fs-5"></i>
                    ),
                  })}
                  renderDetailPanel={({ row }) => {
                    const items = row.original.itemData || [];

                    const hasApprovedOrCompleted = items.some(
                      (item) =>
                        item.status?.toLowerCase() === "approved" ||
                        item.status?.toLowerCase() === "completed",
                    );
                    return (
                      <div
                        style={{
                          padding: "16px 24px",
                          background: "#FAFAFA",
                          maxWidth: "100%",
                          overflowX: "auto",
                        }}
                      >
                        <MaterialReactTable
                          columns={itemColumns}
                          data={row.original.itemData.map((item) => ({
                            ...item,
                            parentData: row.original,
                          }))}
                          state={{
                            columnVisibility: {
                              approvedquantity: hasApprovedOrCompleted,
                            },
                          }}
                          enableTopToolbar={false}
                          enableBottomToolbar={false}
                          enablePagination={false}
                          enableSorting={false}
                          enableColumnFilters={false}
                          enableColumnActions={false}
                          enableDensityToggle={false}
                          enableFullScreenToggle={false}
                          muiTablePaperProps={{
                            elevation: 0,
                            sx: {
                              boxShadow: "none",
                              border: "1px solid #E5E7EB",
                            },
                          }}
                        />
                      </div>
                    );
                  }}
                  muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: "0px",
                      boxShadow: "none",
                    },
                  }}
                />
              </div>
            </Col>

            {CustomViewModal()}
          </Row>
        </Container>
      </div>

      {/* MODAL (UNCHANGED UI) */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{
            borderBottom: "1px solid #FD7545",
            padding: "16px 20px",
          }}
        >
          <span
            style={{
              color: "#FD7545",
              fontWeight: "600",
            }}
          >
            Approve Sales Order
          </span>
        </ModalHeader>

        <ModalBody
          className="overflow-auto"
          style={{ maxHeight: "80vh", padding: "20px 20px 0px 20px" }}
        >
          <SOForm
            closeModal={toggleModal}
            editData={editData}
            isEdit={isEdit}
            isApprove={isApprove}
            refreshSOList={() =>
              dispatch(
                getAllSO(
                  pagination.pageSize,
                  pagination.pageIndex + 1,
                  statusFilter.value,
                  setLoading,
                ),
              )
            }
          />
        </ModalBody>
      </Modal>

      <Modal
        isOpen={stockOutModalOpen}
        toggle={() => setStockOutModalOpen(false)}
        centered
        size="md"
      >
        <ModalHeader
          toggle={() => setStockOutModalOpen(false)}
          style={{
            borderBottom: "1px solid #FD7545",
            padding: "16px 20px",
          }}
        >
          <span
            style={{
              color: "#FD7545",
              fontWeight: "600",
            }}
          >
            Stock Out Item
          </span>
        </ModalHeader>

        <ModalBody>
          <SOStockOutForm
            stockOutData={stockOutData}
            closeModal={() => setStockOutModalOpen(false)}
            refreshSOList={() => {
              dispatch(
                getAllSO(
                  pagination.pageSize,
                  pagination.pageIndex + 1,
                  statusFilter.value,
                  setLoading,
                ),
              );
            }}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

export default NotificationSO;
